import { createSlice, createSelector } from '@reduxjs/toolkit'
import { getRepresentative, getCommunityAllTimeTotals } from '../actions/dashboardActions'
import { getScheduleSessionsByMonth } from '../actions/scheduleSessionActions'
import { getEventsByMonth } from '../actions/eventActions'
import { getDashboardPrograms, getProgramsFilterOptions } from '../actions/programActions'
import moment from 'moment'
import { DATETIME_FORMAT, getMomentInCommunityTimezoneReversed } from '../utility/datesHelper'
import { getCommunityCategories } from '../actions/programCategoryActions'
import { getCommunityActivities, getProgramActivities } from '../actions/activityActions'

const initialState = {
  representative: null,
  sessionsByMonth: {},
  activeCourses: [],
  futureCourses: [],
  programsFilterOptions: [],
  selectedMonthDate: new Date(),
  selectedDate: new Date(),
  communityActivities: [],
  programActivities: [],
  loadingProgramActivities: false,
  eventsByMonth: {},
  communityActivitiesTotalPages: 10,
  programActivitiesTotalPages: 10,
  allTimeTotals: null,
  communityCategories: [],
  loadingCategories: false,
}

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    editCommunityAnnouncement: (state, action) => {
      const updated = action.payload
      state.communityActivities = state.communityActivities.map(activity =>
        activity.id === updated.id ? { ...activity, ...updated } : activity
      )
    },
    editProgramAnnouncement: (state, action) => {
      const updated = action.payload
      state.programActivities = state.programActivities.map(activity =>
        activity.id === updated.id ? { ...activity, ...updated } : activity
      )
    },
    addCommunityAnnouncement: (state, action) => {
      state.communityActivities = [action.payload, ...state.communityActivities]
    },
    addProgramAnnouncement: (state, action) => {
      state.programActivities = [action.payload, ...state.programActivities]
    },
    setSelectedMonthDate: (state, action) => {
      state.selectedMonthDate = action.payload
    },
    setSelectedDate: (state, action) => {
      state.selectedDate = action.payload
    },
    clearCommunityActivities: (state) => {
      state.communityActivities = []
    },
    clearProgramActivities: (state) => {
      state.programActivities = []
    },
    removeCommunityActivity: (state, action) => {
      state.communityActivities = state.communityActivities.filter(activity => activity.id !== action.payload)
    },
    removeProgramActivity: (state, action) => {
      state.programActivities = state.programActivities.filter(activity => activity.id !== action.payload)
    },
  },
  extraReducers: {
    [getRepresentative.fulfilled]: (state, action) => {
      state.representative = action.payload.representative
    },
    [getScheduleSessionsByMonth.fulfilled]: (state, action) => {
      state.sessionsByMonth[state.selectedMonthDate.getMonth()] = action.payload.scheduleSessions
    },
    [getDashboardPrograms.fulfilled]: (state, action) => {
      if (action.payload.filters?.future) {
        state.futureCourses = action.payload.courses
      } else {
        state.activeCourses = action.payload.courses
      }
    },
    [getProgramsFilterOptions.fulfilled]: (state, action) => {
      state.programsFilterOptions = action.payload.courses.map(c => ({ value: c.id, label: c.name, categoryId: c.courseCategoryId }))
    },
    [getCommunityActivities.fulfilled]: (state, action) => {
      state.communityActivitiesTotalPages = action.payload.meta.pagination.total_pages
      state.communityActivities = [...state.communityActivities, ...action.payload.activities]
      state.loadingCommunityActivities = false
    },
    [getProgramActivities.pending]: (state) => {
      state.loadingProgramActivities = true
    },
    [getProgramActivities.rejected]: (state) => {
      state.loadingProgramActivities = false
    },
    [getProgramActivities.fulfilled]: (state, action) => {
      state.programActivitiesTotalPages = action.payload.meta.pagination.total_pages
      state.programActivities = [...state.programActivities, ...action.payload.activities]
      state.loadingProgramActivities = false
    },
    [getEventsByMonth.fulfilled]: (state, action) => {
      state.eventsByMonth[state.selectedMonthDate.getMonth()] = action.payload.events
    },
    [getCommunityAllTimeTotals.fulfilled]: (state, action) => {
      state.allTimeTotals = action.payload
    },
    [getCommunityCategories.pending]: (state) => {
      state.loadingCategories = true
    },
    [getCommunityCategories.fulfilled]: (state, action) => {
      state.communityCategories = action.payload.courseCategories
      state.loadingCategories = false
    },
  },
})

export const {
  addCommunityAnnouncement,
  addProgramAnnouncement,
  setSelectedDate,
  clearCommunityActivities,
  clearProgramActivities,
  setSelectedMonthDate,
  removeProgramActivity,
  removeCommunityActivity,
  editCommunityAnnouncement,
  editProgramAnnouncement,
} = dashboardSlice.actions

export default dashboardSlice.reducer

export const sessionsDates = createSelector(
  state => state.dashboard.sessionsByMonth,
  state => state.dashboard.selectedMonthDate,
  state => state.user.current_user.communities[0].timezone,
  (sessions, selectedMonthDate, timezone) =>
    sessions[selectedMonthDate.getMonth()]?.map(s => getMomentInCommunityTimezoneReversed(moment(s.startDate), timezone, DATETIME_FORMAT).toDate())
      || []
)

export const eventsDates = createSelector(
  state => state.dashboard.eventsByMonth,
  state => state.dashboard.selectedMonthDate,
  state => state.user.current_user.communities[0].timezone,
  (events, selectedMonthDate, timezone) =>
    events[selectedMonthDate.getMonth()]?.map(e => getMomentInCommunityTimezoneReversed(moment(e.eventDate), timezone, DATETIME_FORMAT).toDate())
      || []
)

export const selectedDateEvents = createSelector(
  state => state.dashboard.eventsByMonth,
  state => state.dashboard.selectedDate,
  state => state.user.current_user.communities[0].timezone,
  (
    events,
    selectedDate,
    timezone
  ) => {
    const selectedDateEvents = []
    events[selectedDate.getMonth()]?.forEach(e => {
      const studentsProfiles = e.enrolledUsers.map(u => u.avatar?.fileUrl)
      const event = {
        id: e.id,
        seriesName: e.series.title,
        name: e.name || `Event #${e.order}`,
        format: e.format,
        location: e.location,
        date: getMomentInCommunityTimezoneReversed(moment(e.eventDate), timezone, DATETIME_FORMAT).toDate(),
        students: studentsProfiles,
      }
      if (event.date.toDateString() === selectedDate.toDateString()) {
        selectedDateEvents.push(event)
      }
    })

    return selectedDateEvents
  }
)

export const selectedDateSessions = createSelector(
  state => state.dashboard.sessionsByMonth,
  state => state.dashboard.selectedDate,
  state => state.user.current_user.communities[0].timezone,
  (
    sessions,
    selectedDate,
    timezone
  ) => {
    const selectedDateSessions = []
    sessions[selectedDate.getMonth()]?.forEach(s => {
      const enrollmentsProfiles = s.enrolledUsers.map(u => u.avatar?.fileUrl)
      const session = {
        programTitle: s.course.name,
        programId: s.course.id,
        categoryColor: s.course.courseCategory?.color,
        sessionId: s.id,
        enrollments: s.enrolledUsers.length,
        enrollmentsProfiles: enrollmentsProfiles,
      }
      session.sessionTitle = s.title
      session.sessionStartDate = getMomentInCommunityTimezoneReversed(moment(s.startDate), timezone, DATETIME_FORMAT).toDate()
      session.sessionEndDate = getMomentInCommunityTimezoneReversed(moment(s.endDate), timezone, DATETIME_FORMAT).toDate()
      session.location = s.location
      if (session.sessionStartDate.toDateString() === selectedDate.toDateString()) {
        selectedDateSessions.push(session)
      }
    })

    return selectedDateSessions
  }
)

export const categoriesFilterOptions = createSelector(
  state => state.dashboard.communityCategories,
  categories => categories?.map(c => ({ value: c.id, label: c.name }))
)
